import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as AuditActions from 'audit/AuditActions';
import {AuditLogFormat} from 'audit/AuditDAO';
import {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import Button from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import ResultsPager from 'ui-library/ResultsPager';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

import AuditLogColumnVisibilityDropdown from './AuditLogColumnVisibilityDropdown';
import useAuditLogData from './useAuditLogData';
import useColumnVisibility from './useColumnVisibility';

export interface AuditLogViewModalProps {
  onRequestClose: () => void;
  ids: Array<string>;
  additionalOptions?: {resultsWarehouseIds?: Array<string>; relatedResultsModelId?: string};
}

const PAGE_SIZE = 50;

export default function AuditLogViewModal(props: AuditLogViewModalProps): JSX.Element {
  const {state, allColumns, loadAuditData} = useAuditLogData({
    ids: props.ids,
    pageSize: PAGE_SIZE,
    resultsWarehouseIds: props.additionalOptions?.resultsWarehouseIds,
    relatedResultsModelId: props.additionalOptions?.relatedResultsModelId,
  });
  const {isLoading, auditData, currentPage, error} = state;

  const {hiddenColumns, visibleColumns, handleColumnToggle, handleHideAll, handleShowAll} =
    useColumnVisibility(allColumns);

  const renderMainContent = (): JSX.Element => {
    if (isLoading) {
      return (
        <div className={css(styles.loadingContainer)}>
          <HorizontalThrobber />
        </div>
      );
    }

    if (error) {
      return (
        <div className={css(styles.errorContainer)}>
          <div className={css(styles.errorText)}>{error}</div>
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            onClick={() => {
              loadAuditData(currentPage);
            }}
            size={ButtonSize.MEDIUM}
            text="Retry"
          />
        </div>
      );
    }

    if (!auditData || auditData.data.length === 0) {
      return (
        <div className={css(styles.emptyContainer)}>
          <BenchlingIcon icon="audit-trail" className={css(styles.emptyIcon)} />
          <div className={css(styles.emptyText)}>No audit log entries found.</div>
        </div>
      );
    }

    const {data} = auditData;

    return (
      <Table
        numRows={data.length}
        getRowKey={(rowIndex) => rowIndex.toString()}
        isCompact={true}
        showBorder={false}
        overflowVisible={true}
        frozenHeader={true}
      >
        {visibleColumns.map((column: string) => (
          <Column
            key={column}
            header={column}
            renderCell={(rowIndex: number) => (
              <Cell>
                <div className={css(styles.cellContent)}>{data[rowIndex]?.[column] || 'N/A'}</div>
              </Cell>
            )}
          />
        ))}
      </Table>
    );
  };

  const renderStickyHeader = (): JSX.Element => (
    <div className={css(styles.stickyHeaderContainer)}>
      <div className={css(styles.visibilityAndExportDropdownsContainer)}>
        <AuditLogColumnVisibilityDropdown
          allColumns={allColumns}
          hiddenColumns={hiddenColumns}
          onColumnToggle={handleColumnToggle}
          onHideAll={handleHideAll}
          onShowAll={handleShowAll}
          disabled={isLoading || !auditData}
        />
        <Dropdown
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM} dropLeft={true}>
              <Option
                onSelect={() => {
                  AuditActions.initiateAuditLogExport(
                    props.ids,
                    AuditLogFormat.CSV,
                    props.additionalOptions || {}
                  );
                }}
              >
                Export CSV
              </Option>
              <Option
                onSelect={() => {
                  AuditActions.initiateAuditLogExport(
                    props.ids,
                    AuditLogFormat.PDF,
                    props.additionalOptions || {}
                  );
                }}
              >
                Export PDF
              </Option>
            </Menu>
          }
          closeOnInsideClick={true}
        >
          <Button
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            icon="download"
            size={ButtonSize.SMALL}
            text="Export"
            isDropdown={true}
          />
        </Dropdown>
      </div>
      {/* Normally we would render the ResultsPager using the props provided by SimpleTable. But we want the
      pager to be sticky at the top of the modal along with the table's sticky header */}
      {auditData?.pagination && (
        <ResultsPager
          isLoading={isLoading}
          pagination={{
            offset: currentPage * PAGE_SIZE,
            limit: PAGE_SIZE,
            total: auditData.pagination.total,
          }}
          setOffset={(newOffset: number) => {
            const newPage = Math.floor(newOffset / PAGE_SIZE);
            loadAuditData(newPage);
          }}
        />
      )}
    </div>
  );

  return (
    <Modal
      enableRedesign={true}
      title={auditData?.objectMetadata?.title || 'Audit Log'}
      onRequestClose={props.onRequestClose}
      extraLarge={true}
      scrollable={false}
      expandHeight={false}
      buttons={[
        {
          text: 'Close',
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: props.onRequestClose,
        },
      ]}
      dataTestAttrs={{component: 'AuditLogViewModal', element: 'root'}}
    >
      <div className={css(styles.modalContainer)}>
        {renderStickyHeader()}
        <div className={css(styles.scrollableContainer)}>{renderMainContent()}</div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 220px)',
  },
  stickyHeaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  visibilityAndExportDropdownsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  scrollableContainer: {
    flex: 1,
    overflow: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '300px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    textAlign: 'center',
  },
  errorText: {
    marginBottom: '16px',
    color: Color.GRAY_400,
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '48px',
    color: Color.GRAY_50,
    marginBottom: '16px',
  },
  emptyText: {
    color: Color.GRAY_400,
  },
  cellContent: {
    paddingTop: '10px',
    paddingBottom: '10px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: '1.5',
    fontSize: '14px',
    minHeight: '20px',
  },
});
